import React, { useMemo, useState, Suspense } from 'react';
import { useDeviceSelector } from 'js/store/hooks';
import { prop } from 'ramda';

import { PortalPortlet } from './buzz/components/PortalPortlet';
import { ErrorBoundary } from 'js/app/components/common/ErrorBoundary';
import { PortletTitle } from 'js/components/system/Portlet/components/PortletTitle';
import { UnregisteredPortlet } from 'js/components/system/Portlet/components/UnregisteredPortlet';
import { PortalBanner } from 'js/app/components/main/Registration/components/common/TopBanner/components/PortalBanner';

import { log } from 'log';
import { clsx } from 'js/app/utils/clsx.util';
import { useIsAdmin } from 'js/hooks/user';
import { usePageFetch } from './buzz/hooks/usePageFetch';
import { useMountEffect } from 'js/hooks/useMountEffect';
import { useSegmentation } from 'js/app/hooks/useSegmentation';
import * as helpers from 'js/components/system/Portlet/helpers';
import { componentService } from 'js/services/component.service';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { getCustomPortletStyle } from '@interlayer/js/app/components/system/Portlet/helpers';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { PortletWrapper } from '@interlayer/js/app/components/system/Portlet/components/PortletWrapper/PortletWrapper';
import {
    DYNAMIC_BANNER
} from 'js/app/components/main/Registration/components/common/TopBanner/components/DynamicBanner/constants';

import style from './Portlet.scss';

const typesWithoutTitles = [
    'featured-banner',
    'slot-page'
];

export const Portlet = ({ portlet }) => {
    const isAdmin = useIsAdmin();
    const componentAppearanceSettings = useConfigTag(CONFIG_TAG_TYPE.componentAppearanceSettings, true);
    const [showWrapper, setShowWrapper] = useState(true);
    const [wrapperClassNames, setWrapperClassNames] = useState([]);
    const [isShouldRenderPortlet, setShouldRenderPortlet] = useState(true);

    // buzz added extConfig
    const { config, extConfig, portletId, reactPortletId, portletType, title, column, look, customCSSClassName } = portlet;
    const portlets = componentService.getPageComponents();
    const isWrapperShown = showWrapper || isAdmin;
    const PortletComponent = portlets[portletType] || (isAdmin ? UnregisteredPortlet : null);
    const tkPrefix = helpers.getPortletTkPrefix(portletType);

    const showTitle = typesWithoutTitles.includes(portletType) ? false : portlet?.showTitle;

    // as of react documentation this indeed sometimes produces new objects even if all dependencies are equal
    // delete this after removing usePortlet();
    const portletConfig = useMemo(
        () => ({ ...config, ...extConfig, ...look, reactPortletId, portletId, portletType, tkPrefix, showTitle }),
        [config, extConfig, look, reactPortletId, portletId, portletType, tkPrefix, showTitle]
    );

    const portletInfo = useMemo(
        () => ({ portletId, portletType, tkPrefix, look }),
        [look, portletId, portletType, tkPrefix]
    );

    const portletPreferences = useMemo(
        () => config,
        [config]
    );

    const { page, fetch } = usePageFetch();
    const formFactor = useDeviceSelector(prop('formFactor'));
    const segmentationFilter = useSegmentation();

    const portletStyle = useMemo(() => {
        return getCustomPortletStyle({ tags: componentAppearanceSettings, customCSSClassName });
    }, [componentAppearanceSettings, customCSSClassName]);

    useMountEffect(() => {
        if (!isAdmin && portletConfig.portletType === 'component-copy') {
            fetch(config?.pageUrl);
        }
    }, []);

    if (!isAdmin && page) {
        try {
            const portletCopy = page.portlets.find(
                portletConf => portletConf.portletId === config?.componentId
            );

            if (
                !portletCopy
                || !portletCopy.enabledDevices[formFactor]
                || !segmentationFilter(portletCopy.config?.segmentation)
            ) {
                return null;
            }

            return (
                <ErrorBoundary>
                    <Suspense fallback="">
                        <Portlet
                            portlet={{
                                ...portletCopy,
                                look: { ...portlet?.look },
                                showTitle: portlet?.showTitle,
                                title: portlet?.title
                            }}
                            title={portlet?.title}
                        />
                    </Suspense>
                </ErrorBoundary>
            );
        } catch (e) {
            log('error', e);
            return null;
        }
    }

    if (!PortletComponent || !isShouldRenderPortlet && !isAdmin) {
        return null;
    }

    if (portletConfig.idPortal && portletConfig.portletType === DYNAMIC_BANNER) {
        return (
            <PortalBanner portletConfig={portletConfig} />
        );
    }

    if (portletConfig.idPortal) {
        return (
            <PortalPortlet portletConfig={portletConfig} />
        );
    }

    // id, data-portlet-id, data-react-portlet-id, data-portlet-column, data-portlet-type, data-portlet-title -
    // required for CMS
    return (
        <div
            id={`p_p_id_${portletId}`}
            data-portlet-title={title}
            data-portlet-id={portletId}
            data-portlet-column={column}
            data-portlet-type={portletType}
            data-react-portlet-id={reactPortletId}
            className={
                helpers.getPortletWrapperCSSClassNames(portletConfig, wrapperClassNames, isWrapperShown, customCSSClassName)
            }
        >
            <div className={clsx(style.portlet, helpers.getPortletCSSClassNames(portletConfig))}>
                <PortletWrapper
                    portletStyle={portletStyle}
                >
                    {isWrapperShown && (
                        <PortletTitle portlet={{ ...portlet, showTitle }} configuration={portletStyle.titleConfiguration} />
                    )}
                    <ErrorBoundary>
                        <Suspense fallback="">
                            <PortletComponent
                                key={reactPortletId}
                                config={portletConfig}
                                portletInfo={portletInfo}
                                portletTkPrefix={tkPrefix}
                                setShowWrapper={setShowWrapper} // delete this
                                portletPreferences={portletPreferences}
                                setWrapperClassNames={setWrapperClassNames}
                                wrapperClassNames={wrapperClassNames}
                                setShouldRenderPortlet={setShouldRenderPortlet}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </PortletWrapper>
            </div>
        </div>
    );
};
